/**
 * AdminContractsTab.jsx — Feb 28 2026.
 *
 * Lists writer contracts triggered at the 90K lifetime-words threshold.
 * Admin approves/rejects from this view; approval starts the 15-day
 * withdrawal cooldown on the scribe's earnings page.
 *
 * Also surfaces pending withdrawal invoices so admins can mark them
 * paid once funds are wired externally.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Hourglass, CheckCircle2, XCircle, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_PILL = {
  under_review: 'bg-amber-500/20 text-amber-200 border border-amber-400/40',
  offered:      'bg-indigo-500/20 text-indigo-200 border border-indigo-400/40',
  approved:     'bg-emerald-500/20 text-emerald-200 border border-emerald-400/40',
  rejected:     'bg-rose-500/20 text-rose-200 border border-rose-400/40',
  pending:      'bg-amber-500/20 text-amber-200 border border-amber-400/40',
  paid:         'bg-emerald-500/20 text-emerald-200 border border-emerald-400/40',
};

const AdminContractsTab = () => {
  const [contracts, setContracts] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  const fetchContracts = useCallback(async (status = statusFilter) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/contracts?status=${status}`);
      if (!r.ok) throw new Error('Failed to load contracts');
      const d = await r.json();
      setContracts(d.contracts || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoaded(true);
    }
  }, [statusFilter]);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const filters = useMemo(() => [
    { key: 'all',           label: 'All' },
    { key: 'under_review',  label: 'Under Review' },
    { key: 'offered',       label: 'Offered' },
    { key: 'approved',      label: 'Approved' },
    { key: 'rejected',      label: 'Rejected' },
  ], []);

  const setStatus = async (id, status) => {
    const note = status === 'rejected' ? prompt('Reason for rejection (optional):') || '' : '';
    setBusyId(id);
    setError('');
    try {
      const endpointMap = {
        offered: 'offer',
        approved: 'approve',
        rejected: 'reject',
      };
      const endpoint = endpointMap[status] || status;
      const r = await authFetch(`${BACKEND_URL}/api/admin/contracts/${id}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: note }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `Failed to ${endpoint}`);
      }
      await fetchContracts();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId('');
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="admin-contracts-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-white text-base font-bold flex items-center gap-2">
          <FileText className="w-5 h-5 text-amber-300" />
          Writer Contracts
        </h3>
        <div className="flex flex-wrap gap-1.5" data-testid="admin-contracts-filter">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${
                statusFilter === f.key
                  ? 'bg-amber-500/30 text-amber-100 ring-1 ring-amber-400/50'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
              data-testid={`admin-contracts-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p className="text-rose-400 text-sm" data-testid="admin-contracts-error">{error}</p>
      )}

      {!loaded ? (
        <p className="text-slate-400 text-sm">Loading…</p>
      ) : contracts.length === 0 ? (
        <p className="text-slate-500 text-sm" data-testid="admin-contracts-empty">
          No contracts at this status yet.
        </p>
      ) : (
        <div className="space-y-2" data-testid="admin-contracts-list">
          {contracts.map((c) => (
            <div
              key={c.contract_id}
              className="p-4 rounded-xl bg-slate-800/60 border border-slate-700"
              data-testid={`admin-contract-${c.contract_id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-white font-bold text-sm">User ID: <span className="font-mono text-slate-300">{c.user_id}</span></p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Triggered {fmtDate(c.triggered_at)} · {Number(c.triggered_words || 0).toLocaleString()} lifetime words
                  </p>
                  {c.offered_at && <p className="text-[11px] text-indigo-300 mt-0.5">Offered {fmtDate(c.offered_at)} — awaiting writer signature</p>}
                  {c.approved_at && <p className="text-[11px] text-emerald-300 mt-0.5">
                    Approved {fmtDate(c.approved_at)}{c.signed_by_writer ? ' — signed by writer' : ''}
                  </p>}
                  {c.rejected_at && <p className="text-[11px] text-rose-300 mt-0.5">Rejected {fmtDate(c.rejected_at)}</p>}
                  {c.notes && <p className="text-[11px] text-slate-400 italic mt-1">"{c.notes}"</p>}
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold ${STATUS_PILL[c.status] || 'bg-slate-700 text-slate-300'}`}>
                  {(c.status || 'unknown').replace('_', ' ')}
                </span>
              </div>
              {c.status === 'under_review' && (
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    size="sm"
                    onClick={() => setStatus(c.contract_id, 'offered')}
                    disabled={busyId === c.contract_id}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid={`admin-contract-offer-${c.contract_id}`}
                    title="Send this writer the T&C + Contract to sign"
                  >
                    <FileText className="w-4 h-4 mr-1" /> Offer Contract
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setStatus(c.contract_id, 'rejected')}
                    disabled={busyId === c.contract_id}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid={`admin-contract-reject-${c.contract_id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                  <Hourglass className="w-4 h-4 text-amber-300 ml-1" />
                </div>
              )}
              {c.status === 'offered' && (
                <div className="flex items-center gap-2 mt-3">
                  <p className="text-[11px] text-indigo-300 italic flex items-center gap-1.5">
                    <Hourglass className="w-3.5 h-3.5 animate-pulse" />
                    Waiting for the writer to accept the T&amp;C and sign.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminContractsTab;
